import type {
  HealthResponse,
  XeroStatus,
  QuotaPolicy,
  QuotaResult,
  ExclusionCategory,
  QuotaScheduleRow,
  QuotaRunResponse,
} from '@cca/xero-shared';
import type { AuditSession } from '@cca/ui';
import { reportTelemetry } from '@cca/ui';
import type { BudgetData } from '../types';
import type {
  CashflowData,
  CashflowBankAccount,
  BankSyncResult,
  XeroMonthlyActuals,
} from '../cashflowTypes';

// The shared quota response DTOs live in @cca/xero-shared (one pinned contract for
// both quota workers) — re-exported here so existing imports of this module keep
// working.
export type { ExclusionCategory, QuotaScheduleRow, QuotaRunResponse };

export interface InboxFile {
  id: string;
  name: string;
  mimeType: string;
  size: number;
  createdDate: string;
}

export interface BankAccount {
  id: string;
  name: string;
  code: string;
  currencyCode: string;
}

export interface TxnMatch {
  id: string;
  objectGroup: string;
  dateStr: string;
  amount: number;
  type: string;
  contact: string;
  reference: string;
  confidence: 'HIGH' | 'MEDIUM' | 'LOW';
  daysDiff: number | null;
}

export interface BankTxn {
  id: string;
  objectGroup: string;
  dateStr: string;
  amount: number;
  type: string;
  contact: string;
  reference: string;
}

export interface ContactSuggestion {
  id: string;
  name: string;
}

export interface ParseResult {
  ok?: boolean;
  error?: string;
  fileId: string;
  originalName: string;
  newName?: string;
  vendor?: string;
  amount?: string;
  date?: string;
}

export interface BillResult {
  ok?: boolean;
  error?: string;
  invoiceId?: string;
  billUrl?: string;
  attachWarning?: string;
  needsContactConfirmation?: boolean;
  vendorName?: string;
  suggestions?: ContactSuggestion[];
  parsedAmount?: number;
  parsedDate?: string;
  fileId?: string;
  fileName?: string;
}

export interface ParseMatchResult {
  ok?: boolean;
  error?: string;
  fileId: string;
  vendor?: string;
  amount?: string;
  date?: string;
  matches?: TxnMatch[];
}

export interface InboxSetup {
  xeroConnected: boolean;
  xeroOrgName: string;
  anthropicKey: boolean;
  archiveFolderId: string;
  lockedCount: number;
}

const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '';

function sessionToken(): string | null {
  return localStorage.getItem('xero_session_token');
}

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const token = sessionToken();
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      ...init,
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        ...(token ? { 'X-Session-Token': token } : {}),
        ...init?.headers,
      },
    });
  } catch (e) {
    // Network failure (offline, DNS, CORS) — capture before rethrowing.
    reportTelemetry('api_error', `Network failure on ${path}`, path);
    throw e;
  }
  if (res.status === 401) {
    // Session expired — clear local storage so App re-renders login screen.
    // Not telemetry-worthy: it's an expected, handled state.
    localStorage.removeItem('xero_session_token');
    localStorage.removeItem('xero_session_role');
    window.dispatchEvent(new CustomEvent('xero:session-expired'));
    throw new Error('session_expired');
  }
  if (!res.ok) {
    reportTelemetry('api_error', `API ${res.status} on ${path}`, path);
    throw new Error(`API ${res.status} on ${path}`);
  }
  return res.json() as Promise<T>;
}

export const api = {
  // ── Auth ─────────────────────────────────────────────────────────────────
  // OTP sign-in (request/verify) is owned by the shared @cca/ui <LoginScreen>,
  // which talks to /api/auth/otp/* directly (ADR-018 / AUTH-8) — the bespoke
  // authRequestOtp/authVerifyOtp client helpers retired with the old LoginPage.
  authLogout: () =>
    apiFetch<{ ok: boolean }>('/api/auth/logout', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: '{}',
    }),

  /** "Sign out all devices" — ends every session for the caller's email. */
  authLogoutAll: () =>
    apiFetch<{ ok: boolean }>('/api/auth/logout-all', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: '{}',
    }),

  authMe: () => apiFetch<{ email: string; role: string; isRegistrar: boolean }>('/api/auth/me'),

  /** Registrar-only login + activity audit trail. */
  loginHistory: () => apiFetch<{ sessions: AuditSession[] }>('/api/auth/login-history'),

  // ── Health / Xero ─────────────────────────────────────────────────────────
  health: () => apiFetch<HealthResponse>('/api/health'),
  xeroStatus: () => apiFetch<XeroStatus>('/api/xero/status'),
  xeroDisconnect: () => apiFetch<{ ok: boolean }>('/api/xero/connect', { method: 'DELETE' }),

  inboxSetup: () => apiFetch<InboxSetup>('/api/inbox/setup'),
  inboxFiles: () => apiFetch<{ ok: boolean; files: InboxFile[] }>('/api/inbox/files'),
  inboxLocked: () => apiFetch<{ ids: string[] }>('/api/inbox/locked'),
  inboxUnlock: (fileId: string) =>
    apiFetch<{ ok: boolean }>(`/api/inbox/locked/${fileId}`, { method: 'DELETE' }),

  inboxRename: (fileId: string, name: string) =>
    apiFetch<{ ok: boolean }>(`/api/inbox/files/${fileId}/rename`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name }),
    }),

  inboxParse: (fileId: string, fileName: string, mimeType: string) =>
    apiFetch<ParseResult>(`/api/inbox/files/${fileId}/parse`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fileName, mimeType }),
    }),

  inboxCreateBill: (fileId: string, fileName: string, mimeType: string) =>
    apiFetch<BillResult>(`/api/inbox/files/${fileId}/bill`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fileName, mimeType }),
    }),

  inboxCreateBillWithContact: (args: {
    fileId: string;
    vendorName: string;
    contactId?: string;
    amount?: number;
    date?: string;
  }) =>
    apiFetch<BillResult>('/api/inbox/bill', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(args),
    }),

  inboxBankAccounts: () =>
    apiFetch<{ ok: boolean; accounts: BankAccount[] }>('/api/inbox/bank-accounts'),

  inboxLoadTxns: (accountId: string) =>
    apiFetch<{
      ok: boolean;
      count: number;
      txnCount: number;
      paymentCount: number;
      paymentError?: string;
    }>('/api/inbox/bank-transactions/load', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ accountId }),
    }),

  inboxTxnsForRange: (accountId: string, from: string, to: string) =>
    apiFetch<{ ok: boolean; transactions: BankTxn[] }>(
      `/api/inbox/bank-transactions/range?accountId=${accountId}&from=${from}&to=${to}`,
    ),

  inboxParseMatch: (fileId: string, fileName: string, mimeType: string, accountId: string) =>
    apiFetch<ParseMatchResult>(`/api/inbox/files/${fileId}/parse-match`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fileName, mimeType, accountId }),
    }),

  inboxConfirmMatch: (fileId: string, objectId: string, objectGroup: string) =>
    apiFetch<{ ok: boolean }>(`/api/inbox/files/${fileId}/confirm-match`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ objectId, objectGroup }),
    }),

  // ── Budget ──────────────────────────────────────────────────────────────

  budgetGetData: () =>
    apiFetch<BudgetData>('/api/budget/data').then((data) => ({
      data,
      source: 'saved' as const,
    })),

  budgetSaveData: (payload: BudgetData) =>
    apiFetch<BudgetData>('/api/budget/data', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }).then((data) => ({ data, source: 'saved' as const })),

  budgetGLStatus: () => apiFetch<{ count: number; lastSyncedAt: string }>('/api/budget/gl/status'),

  budgetSyncGL: () =>
    apiFetch<{
      count: number;
      lastSyncedAt: string;
      sample: string[];
      forBudget: { code: string; name: string; kind: 'income' | 'expense' }[];
      gstProfilesUpdated?: number;
      gstProfilesSkipped?: number;
    }>('/api/budget/gl/sync', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: '{}',
    }),

  budgetGLAccounts: () =>
    apiFetch<{ code: string; name: string; kind: 'income' | 'expense' }[]>(
      '/api/budget/gl/accounts',
    ),

  budgetPLStatus: () =>
    apiFetch<{ count: number; lastImportedAt: string; periods: string[]; sourceFile: string }>(
      '/api/budget/pl/status',
    ),

  budgetPLImport: (payload: {
    periods: string[];
    rows: { code: string; name: string; section: string; values: number[] }[];
    sourceFile: string;
  }) =>
    apiFetch<{
      count: number;
      accountCount: number;
      lastImportedAt: string;
      periods: string[];
      sourceFile: string;
    }>('/api/budget/pl/import', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }),

  budgetPLActuals: () =>
    apiFetch<{
      periods: string[];
      byCode: Record<string, Record<string, number>>;
      nameByCode: Record<string, string>;
    }>('/api/budget/pl/actuals'),

  budgetXlsxImport: (payload: {
    pAndL: {
      periods: string[];
      rows: { code: string; name: string; section: string; values: number[] }[];
      sourceFile?: string;
    } | null;
    performance: {
      periods: string[];
      rows: { code: string; name: string; section: string; values: number[] }[];
    } | null;
    churchIncome: {
      years: number[];
      churches: { name: string; byYear: Record<number, number> }[];
    } | null;
    sourceFile: string;
  }) =>
    apiFetch<{
      pAndL: {
        count: number;
        accountCount: number;
        lastImportedAt: string;
        periods: string[];
        sourceFile: string;
      } | null;
      performance: {
        rowCount: number;
        accountCount: number;
        periods: string[];
        importedAt: string;
      } | null;
      churchIncome: { rowCount: number; sourceFile: string } | null;
      sourceFile: string;
      importedAt: string;
    }>('/api/budget/xlsx/import', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }),

  budgetPerformance: () =>
    apiFetch<{
      periods: string[];
      byCode: Record<string, Record<string, number>>;
      sectionByCode: Record<string, string>;
      sectionsInOrder: { name: string; kind: 'income' | 'expense' }[];
    }>('/api/budget/performance'),

  budgetCashByMonth: () => apiFetch<XeroMonthlyActuals>('/api/budget/cash-by-month'),

  budgetSaveCashByMonth: (
    updates: { code: string; ym: string; amount: number }[],
    lastFinalisedMonth: string,
  ) =>
    apiFetch<{ ok: boolean; written: number }>('/api/budget/cash-by-month', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates, lastFinalisedMonth }),
    }),

  budgetCashflowGet: () => apiFetch<CashflowData>('/api/budget/cashflow'),

  budgetCashflowSave: (payload: CashflowData) =>
    apiFetch<CashflowData>('/api/budget/cashflow', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }),

  budgetUpdateBankBalances: (balances: { id: string; currentBalance: number }[]) =>
    apiFetch<CashflowBankAccount[]>('/api/budget/cashflow/bank-balances', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ balances }),
    }),

  budgetBankSync: () => apiFetch<BankSyncResult>('/api/budget/cashflow/bank-sync'),

  budgetApplyGst: () =>
    apiFetch<{ updated: number; skipped: number }>('/api/budget/cashflow/gst-apply', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: '{}',
    }),

  // ── Receipts ─────────────────────────────────────────────────────────────

  receiptsConfig: () =>
    apiFetch<{
      config: { key: string; value: string; updated_at: string; updated_by: string | null }[];
      knownKeys: string[];
    }>('/api/receipts/config'),

  receiptsConfigSet: (key: string, value: string) =>
    apiFetch<{ ok: boolean; key: string; value: string }>(
      `/api/receipts/config/${encodeURIComponent(key)}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ value }),
      },
    ),

  receiptsPeriods: () =>
    apiFetch<{
      periods: {
        id: string;
        label: string;
        start_date: string;
        end_date: string;
        issue_date: string;
        number_prefix: string;
        next_number: number;
        closed_at: string | null;
        created_at: string;
      }[];
    }>('/api/receipts/periods'),

  receiptsCreatePeriod: (body: {
    id: string;
    label: string;
    start_date: string;
    end_date: string;
    issue_date: string;
    number_prefix: string;
  }) =>
    apiFetch<{ ok: boolean; id: string }>('/api/receipts/periods', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),

  receiptsSetNextNumber: (id: string, nextNumber: number) =>
    apiFetch<{ ok: boolean; id: string; next_number: number }>(
      `/api/receipts/periods/${encodeURIComponent(id)}/next-number`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ next_number: nextNumber }),
      },
    ),

  receiptsXeroTrackingOptions: () =>
    apiFetch<{ xeroConnected: boolean; options: string[]; error?: string }>(
      '/api/receipts/xero-tracking-options',
    ),

  receiptsTrackingMappings: () =>
    apiFetch<{ mappings: { category: string; reason: string; updated_at: string }[] }>(
      '/api/receipts/tracking-mappings',
    ),

  receiptsTrackingMappingSet: (category: string, reason: string) =>
    apiFetch<{ ok: boolean; category: string; reason: string }>(
      `/api/receipts/tracking-mappings/${encodeURIComponent(category)}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason }),
      },
    ),

  receiptsTrackingMappingDelete: (category: string) =>
    apiFetch<{ ok: boolean; category: string }>(
      `/api/receipts/tracking-mappings/${encodeURIComponent(category)}`,
      { method: 'DELETE', headers: { 'X-Requested-With': 'XMLHttpRequest' } },
    ),

  receiptsPull: (periodId: string) =>
    apiFetch<{
      ok: boolean;
      periodId: string;
      pulledAt: string;
      aggregateCount: number;
      uniqueDonorCount: number;
      txnCount: number;
      totalsByCurrency: Record<string, number>;
    }>('/api/receipts/pull', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ periodId }),
    }),

  receiptsDonors: (periodId: string) =>
    apiFetch<{
      periodId: string;
      aggregateCount: number;
      uniqueDonorCount: number;
      aggregates: {
        contactId: string;
        displayName: string;
        email: string | null;
        address: {
          line1: string | null;
          line2: string | null;
          city: string | null;
          postcode: string | null;
        };
        currency: string;
        totalAmount: number;
        txnCount: number;
        primaryTracking: string | null;
        warnings: unknown[];
        pulledAt: string;
      }[];
    }>(`/api/receipts/donors?periodId=${encodeURIComponent(periodId)}`),

  receiptsIssueAndDraft: (periodId: string, contactId: string, currency?: string) =>
    apiFetch<{
      ok: boolean;
      receiptId: string;
      receiptNumber: string;
      draftId: string;
      messageId: string;
      pdfR2Key: string;
      pdfSha256: string;
      to: string;
    }>('/api/receipts/issue-and-draft', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ periodId, contactId, currency }),
    }),

  receiptsListReceipts: (periodId: string) =>
    apiFetch<{
      periodId: string;
      receipts: {
        id: string;
        receipt_number: string;
        contact_id: string;
        display_name: string;
        email: string | null;
        total_amount: number;
        currency: string;
        txn_count: number;
        status: string;
        supersedes: string | null;
        tracking_label: string | null;
        email_sent_at: string | null;
        xero_attachment_id: string | null;
        created_at: string;
        error_detail: string | null;
      }[];
    }>(`/api/receipts/receipts?periodId=${encodeURIComponent(periodId)}`),

  receiptsReissue: (receiptId: string) =>
    apiFetch<{
      ok: boolean;
      receiptId: string;
      receiptNumber: string;
      draftId: string;
      messageId: string;
      pdfR2Key: string;
      pdfSha256: string;
      to: string;
      supersededId: string;
    }>(`/api/receipts/receipts/${encodeURIComponent(receiptId)}/reissue`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: '{}',
    }),

  receiptsXeroAttach: (receiptId: string) =>
    apiFetch<{ ok: boolean; receiptId: string; attachmentId: string }>(
      `/api/receipts/receipts/${encodeURIComponent(receiptId)}/xero-attach`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' },
    ),

  receiptsPatchDonor: (
    contactId: string,
    patch: {
      email?: string | null;
      addr_line1?: string | null;
      addr_line2?: string | null;
      city?: string | null;
      postcode?: string | null;
    },
  ) =>
    apiFetch<{
      ok: boolean;
      donor: {
        contact_id: string;
        display_name: string;
        email: string | null;
        addr_line1: string | null;
        addr_line2: string | null;
        city: string | null;
        postcode: string | null;
      };
    }>(`/api/receipts/donors/${encodeURIComponent(contactId)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    }),

  receiptsClosePeriod: (id: string) =>
    apiFetch<{ ok: boolean; id: string }>(`/api/receipts/periods/${encodeURIComponent(id)}/close`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: '{}',
    }),

  // ── Reimburse ─────────────────────────────────────────────────────────────

  reimburseActivities: () => apiFetch<{ activities: string[] }>('/api/reimburse/activities'),

  reimburseSubmissions: () =>
    apiFetch<{
      submissions: {
        id: string;
        submitted_at: string;
        full_name: string;
        email: string;
        phone: string | null;
        xero_invoice_id: string | null;
        xero_invoice_num: string | null;
        xero_status: string | null;
        pdf_r2_key: string | null;
        expense_count: number;
        total_amount: number;
      }[];
    }>('/api/reimburse/submissions'),

  reimburseSubmission: (id: string) =>
    apiFetch<{
      submission: {
        id: string;
        submitted_at: string;
        full_name: string;
        email: string;
        phone: string | null;
        bank_account: string | null;
        bank_account_name: string | null;
        xero_contact_id: string | null;
        xero_invoice_id: string | null;
        xero_invoice_num: string | null;
        xero_status: string | null;
        pdf_r2_key: string | null;
      };
      expenses: {
        id: string;
        expense_num: number;
        date: string | null;
        activity: string | null;
        supplier: string | null;
        description: string | null;
        amount: number;
        gst: string | null;
        receipt_r2_key: string | null;
        status: string;
        notes: string | null;
      }[];
    }>(`/api/reimburse/submissions/${encodeURIComponent(id)}`),

  reimburseUpdateStatus: (expenseId: string, status: string, notes?: string) =>
    apiFetch<{ ok: boolean; expenseId: string; newStatus: string; overallStatus: string }>(
      `/api/reimburse/expenses/${encodeURIComponent(expenseId)}/status`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status, notes }),
      },
    ),

  reimburseConfigActivities: () =>
    apiFetch<{ activities: string[] }>('/api/reimburse/config/activities'),

  reimburseConfigActivitiesSave: (activities: string[]) =>
    apiFetch<{ ok: boolean; activities: string[] }>('/api/reimburse/config/activities', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ activities }),
    }),

  reimburseConfigAccountCodes: () =>
    apiFetch<{ accountCodes: Record<string, string> }>('/api/reimburse/config/account-codes'),

  reimburseConfigAccountCodesSave: (accountCodes: Record<string, string>) =>
    apiFetch<{ ok: boolean; accountCodes: Record<string, string> }>(
      '/api/reimburse/config/account-codes',
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ accountCodes }),
      },
    ),
  reimburseXeroSync: (submissionId: string) =>
    apiFetch<{
      ok: boolean;
      alreadySynced?: boolean;
      invoiceId?: string;
      invoiceNumber?: string | null;
      contactId?: string;
      contactState?: 'existing' | 'new';
    }>(`/api/reimburse/submissions/${encodeURIComponent(submissionId)}/xero-sync`, {
      method: 'POST',
    }),

  // ── Diocesan Quota ──────────────────────────────────────────────────────────
  quotaChurches: () => apiFetch<QuotaChurch[]>('/api/quota/churches'),

  quotaFinancials: (churchId: string) =>
    apiFetch<QuotaFinancialYear[]>(`/api/quota/financials/${encodeURIComponent(churchId)}`),

  quotaCurate: (patch: QuotaCuratePatch) =>
    apiFetch<{ ok: boolean }>('/api/quota/curate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    }),

  quotaManualYear: (body: {
    church_id: string;
    financial_year: number;
    income: number;
    note?: string | null;
  }) =>
    apiFetch<{ ok: boolean }>('/api/quota/manual-year', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),

  // Set/clear a PROVISIONAL Register gross on an existing year with no official
  // figure yet (source_return_id null). Overwritten by the next ingest. 409 if
  // the year is missing or already holds official (locked) Register data.
  quotaSetProvisionalGross: (body: {
    church_id: string;
    financial_year: number;
    gross: number | null;
  }) =>
    apiFetch<{ ok: boolean }>('/api/quota/provisional-gross', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),

  quotaSyncChurches: () =>
    apiFetch<{ synced: number; with_registration: number; deactivated: number }>(
      '/api/quota/sync-churches',
      { method: 'POST' },
    ),

  quotaIngest: () =>
    apiFetch<{ count: number; flagged: number }>('/api/quota/ingest', { method: 'POST' }),

  // Push the quota dataset into the isolated Quota Model consultation app's D1
  // (the "populate" control). One-directional; the model app holds no creds.
  quotaPushToModel: () =>
    apiFetch<{ ok: boolean; churches: number; financials: number }>('/api/quota/push-to-model', {
      method: 'POST',
    }),

  // Read-only window into the open Quota Model app: who's taken part and what
  // they've saved/run. Powers the "Model Activity" button on the Quota page.
  quotaModelActivity: () => apiFetch<ModelActivity>('/api/quota/model-activity'),

  // ── Quota: exclusion categories + per-year amounts ─────────────────────────
  quotaCategories: () => apiFetch<ExclusionCategory[]>('/api/quota/categories'),

  quotaUpsertCategory: (cat: ExclusionCategory) =>
    apiFetch<{ ok: boolean; category_key: string }>('/api/quota/categories', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(cat),
    }),

  quotaSetExclusionAmount: (body: {
    church_id: string;
    financial_year: number;
    category_key: string;
    amount: number;
    source?: string;
  }) =>
    apiFetch<{ ok: boolean }>('/api/quota/exclusion-amount', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),

  quotaExclusionExport: () => apiFetch<ExclusionExportRow[]>('/api/quota/exclusion-export'),

  quotaBulkSetExclusionAmounts: (rows: BulkAmountRow[]) =>
    apiFetch<{ ok: boolean; written: number; received: number }>(
      '/api/quota/exclusion-amounts/bulk',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rows }),
      },
    ),

  // ── Quota: policies, run, what-if ──────────────────────────────────────────
  quotaPolicies: (includeArchived = false) =>
    apiFetch<QuotaPolicySummary[]>(`/api/quota/policies${includeArchived ? '?archived=1' : ''}`),

  quotaPolicy: (version: string) =>
    apiFetch<QuotaPolicy>(`/api/quota/policy/${encodeURIComponent(version)}`),

  quotaSavePolicy: (policy: QuotaPolicy) =>
    apiFetch<{ ok: boolean; policy_version: string }>('/api/quota/policy', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(policy),
    }),

  quotaArchivePolicy: (version: string) =>
    apiFetch<{ ok: boolean }>(`/api/quota/policy/${encodeURIComponent(version)}/archive`, {
      method: 'POST',
    }),

  quotaRestorePolicy: (version: string) =>
    apiFetch<{ ok: boolean }>(`/api/quota/policy/${encodeURIComponent(version)}/restore`, {
      method: 'POST',
    }),

  quotaSetPolicyNote: (version: string, note: string) =>
    apiFetch<{ ok: boolean }>(`/api/quota/policy/${encodeURIComponent(version)}/note`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ note }),
    }),

  // Permanent delete — server only allows it for already-archived policies.
  quotaDeleteArchivedPolicy: (version: string) =>
    apiFetch<{ ok: boolean }>(`/api/quota/policy/${encodeURIComponent(version)}`, {
      method: 'DELETE',
    }),

  quotaRun: (policy_version: string, assessment_year: number) =>
    apiFetch<QuotaRunResponse>('/api/quota/run', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ policy_version, assessment_year }),
    }),

  quotaWhatif: (policy: QuotaPolicy, assessment_year: number) =>
    apiFetch<QuotaRunResponse>('/api/quota/whatif', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ policy, assessment_year }),
    }),

  quotaResult: (churchId: string, year: number, policy: string) =>
    apiFetch<QuotaResult>(
      `/api/quota/result/${encodeURIComponent(churchId)}?year=${year}&policy=${encodeURIComponent(policy)}`,
    ),

  quotaVerify: (churchId: string, year: number, policy: string) =>
    apiFetch<{ reproducible: boolean; stored?: QuotaResult; recomputed?: QuotaResult }>(
      `/api/quota/verify/${encodeURIComponent(churchId)}?year=${year}&policy=${encodeURIComponent(policy)}`,
    ),
};

// ── Quota types ───────────────────────────────────────────────────────────────
// Read-only summary of the open Quota Model app's activity (see quotaModelActivity).
export interface ModelActivity {
  bound: boolean;
  open: boolean;
  generatedAt: string;
  counts: { participants: number; policies: number; runs: number };
  participants: {
    name: string;
    email: string;
    church: string | null;
    role: string;
    lastSeenAt: string | null;
    policyCount: number;
  }[];
  policies: { name: string; who: string; church: string | null; updatedAt: string }[];
  runs: { who: string; detail: string | null; at: string }[];
}

export interface QuotaChurch {
  church_id: string;
  zoho_account_id: string | null;
  charity_registration_number: string | null;
  name: string;
  quota_payable: boolean;
  current_quota: number | null;
  financial_year_end: string | null;
  /** The financial year the list watches (previous NZ year). */
  prior_year?: number;
  /** Where that year's Register gross stands: `official` (locked Register
   *  data), `manual` (provisional/manual figure), or `missing` (none yet). */
  prior_year_gross?: 'official' | 'manual' | 'missing';
}

export interface QuotaFinancialYear {
  financial_year: number;
  charity_registration_number: string | null;
  is_manual: boolean;
  total_gross_income: number | null;
  /** Register annual-return id. NULL = no official Register figure yet, so
   *  total_gross_income (if set) is a registrar PROVISIONAL value the next ingest
   *  will overwrite. Non-null = official → the Register gross is locked. */
  source_return_id: string | null;
  total_expenditure: number | null;
  excluded_income: number;
  include_in_assessment: boolean;
  income_override: number | null;
  note: string | null;
  reporting_tier: string | null;
  summary_url: string | null;
  donations_koha: number | null;
  bequests: number | null;
  capital_grants_donations: number | null;
  govt_grants: number | null;
  grants_sponsorship: number | null;
  trading_income: number | null;
  membership_fees: number | null;
  other_income: number | null;
  fundraising_expenses: number | null;
  net_surplus: number | null;
  /** Exclusion amount per category_key for this year (0 where unset). */
  category_amounts: Record<string, number>;
  effective_assessable: number | null;
}

export interface QuotaCuratePatch {
  church_id: string;
  financial_year: number;
  include_in_assessment?: boolean;
  income_override?: number | null;
  note?: string | null;
}

export interface ExclusionExportRow {
  church_id: string;
  name: string;
  registration_number: string | null;
  financial_year: number;
  total_gross_income: number | null;
  category_amounts: Record<string, number>;
}

export interface BulkAmountRow {
  church_id: string;
  financial_year: number;
  category_key: string;
  amount: number;
}

export interface QuotaPolicySummary {
  policy_version: string;
  effective_assessment_year: number | null;
  created_at: string;
  label: string | null;
  archived: boolean;
}

// QuotaScheduleRow / QuotaRunResponse now live in @cca/xero-shared (one pinned
// contract for both quota workers + both web apps); re-exported at the top of
// this module.
